'use client';

import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AppIcon } from '@impacta/shared';
import type { FAQItem } from '@/data/parseFaqs';

interface FaqAccordionProps {
  faqs: FAQItem[];
  accentColor: string;
  accentLight?: string;
  accentBg?: string;
  accentBorder?: string;
  cta?: React.ReactNode;
  maxHeight?: number;
}

export default function FaqAccordion({
  faqs,
  accentColor,
  accentLight,
  accentBg,
  accentBorder,
  cta,
  maxHeight = 400,
}: FaqAccordionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const toggle = (index: number) => setOpenIndex((prev) => (prev === index ? null : index));

  const light = accentLight ?? `${accentColor}1A`; // 10% opacity hex
  const bg = accentBg ?? `${accentColor}0D`; // 5% opacity hex
  const border = accentBorder ?? `${accentColor}33`; // 20% opacity hex

  return (
    <section id="faq" className="py-24 bg-white">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-14">
          <p
            className="text-sm font-bold uppercase tracking-widest mb-3"
            style={{ color: accentColor }}
          >
            Resolvemos tus dudas
          </p>
          <h2 className="text-3xl lg:text-4xl font-extrabold text-[#373A36] mb-4">
            Preguntas <span style={{ color: accentColor }}>Frecuentes</span>
          </h2>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <div
                key={index}
                className={`rounded-2xl border transition-all duration-200 overflow-hidden ${
                  isOpen ? 'shadow-md' : ''
                }`}
                style={{
                  borderColor: isOpen ? accentColor : '#E8E8E4',
                  background: isOpen ? bg : 'white',
                }}
                onMouseEnter={(e) => {
                  if (!isOpen) {
                    (e.currentTarget as HTMLDivElement).style.borderColor = accentColor;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isOpen) {
                    (e.currentTarget as HTMLDivElement).style.borderColor = '#E8E8E4';
                  }
                }}
              >
                <button
                  onClick={() => toggle(index)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 rounded"
                  style={{ outlineColor: accentColor }}
                  aria-expanded={isOpen}
                >
                  <span
                    className="text-sm font-semibold leading-snug transition-colors duration-200"
                    style={{ color: isOpen ? accentColor : '#373A36' }}
                  >
                    {faq.question}
                  </span>
                  <span
                    className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200"
                    style={{
                      background: isOpen ? accentColor : light,
                      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  >
                    <AppIcon
                      name="ChevronDownIcon"
                      size={16}
                      style={{ color: isOpen ? 'white' : accentColor }}
                    />
                  </span>
                </button>
                <div
                  className="overflow-hidden transition-all duration-300 ease-in-out"
                  style={{ maxHeight: isOpen ? `${maxHeight}px` : '0px' }}
                >
                  <div className="px-6 pb-5">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: ({ children }) => (
                          <p className="text-sm text-[#6b6e68] leading-relaxed mb-3 last:mb-0">
                            {children}
                          </p>
                        ),
                        strong: ({ children }) => (
                          <strong className="font-semibold text-[#373A36]">{children}</strong>
                        ),
                        a: ({ href, children }) => (
                          <a
                            href={href}
                            className="underline transition-colors"
                            style={{ color: accentColor }}
                          >
                            {children}
                          </a>
                        ),
                        ul: ({ children }) => (
                          <ul className="list-disc pl-5 text-[#6b6e68] text-sm leading-relaxed mb-3 space-y-1">
                            {children}
                          </ul>
                        ),
                        li: ({ children }) => <li>{children}</li>,
                      }}
                    >
                      {faq.answer}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {cta && (
          <div
            className="mt-12 text-center p-8 rounded-3xl"
            style={{
              background: `linear-gradient(135deg, ${light} 0%, ${bg} 100%)`,
              border: `1px solid ${border}`,
            }}
          >
            {cta}
          </div>
        )}
      </div>
    </section>
  );
}
